import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import classNames from "classnames";
import { ProductSelectionDto } from "definitions/product-storage/models/product-selection-dto";
import Skeleton from "react-loading-skeleton";

import { ProductTableData } from "../../store/fetchers";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./components";
import css from "./ProductTable.module.scss";
import { getColumns } from "./ProductTableColumns";

interface ProductsTableProps {
  products: ProductTableData[];
  productSample: ProductSelectionDto | null;
  isLoading?: boolean;
  onRowClick?: (row: ProductTableData) => void;
  selectedIds?: Set<number>;
  setSelectedIds: (ids: number[]) => void;
}

export const ProductsTable = ({
  products,
  productSample,
  isLoading,
  onRowClick,
  selectedIds,
  setSelectedIds,
}: ProductsTableProps) => {
  const columns = getColumns(
    selectedIds || new Set<number>(),
    products,
    productSample,
    setSelectedIds,
  );
  const table = useReactTable({
    data: products,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading && !products?.length) {
    return (
      <div className={css.tableWrapper}>
        <Skeleton
          count={9}
          height={60}
          containerClassName={css.skeletonContainer}
          className={css.skeletonCard}
        />
      </div>
    );
  }

  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;

  return (
    <div className={classNames("g-table", css.tableWrapper)}>
      <Table sticky>
        <TableHeader>
          {headerGroups.map((group) => (
            <TableRow key={group?.id}>
              {group.headers.map((header) => {
                const size = header.getSize();
                return (
                  <TableHead
                    key={header?.id}
                    colSpan={header.colSpan}
                    style={{ width: size }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {rows.map((row) => {
            const product = row.original;
            const cells = row.getVisibleCells();

            return (
              <TableRow
                key={row?.id}
                onClick={() => {
                  if (onRowClick) {
                    onRowClick(product);
                  }
                }}
              >
                {cells.map((cell) => {
                  return (
                    <TableCell key={cell?.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
